// Age cards. On every age advance the player is shown one card from each of
// the three directions (economy / military / knowledge) and keeps exactly one.
// There is no cost beyond opportunity: the two you pass are gone for the run.
// Effects mutate the run modifiers, which last until the settlement falls.

/** Per-run multipliers and flat bonuses that cards (and later artifacts) feed.
 *  Multipliers default to 1, flats to 0. A fresh run resets all of these. */
export interface RunModifiers {
  productionMult: number; // global passive + active resource production
  harvestDamageMult: number; // cursor circle damage
  harvestRadiusMult: number; // cursor circle radius
  harvestYieldMult: number; // resource dropped per node depletion (artifacts)
  autoCollectMult: number; // workforce gathering rate
  defenseMult: number; // multiplies final settlement defense
  defenseFlat: number; // added to raw defense before multipliers
  knowledgeMult: number; // knowledge production and tech factor
  buildCostMult: number; // scales building costs (lower is cheaper)
  popGrowthMult: number; // population growth speed
  waveIntervalMult: number; // scales time between waves (artifacts can slow them)
}

export function freshRunModifiers(): RunModifiers {
  return {
    productionMult: 1,
    harvestDamageMult: 1,
    harvestRadiusMult: 1,
    harvestYieldMult: 1,
    autoCollectMult: 1,
    defenseMult: 1,
    defenseFlat: 0,
    knowledgeMult: 1,
    buildCostMult: 1,
    popGrowthMult: 1,
    waveIntervalMult: 1,
  };
}

export type CardCategory = 'economy' | 'military' | 'knowledge';

export interface AgeCard {
  id: string;
  name: string;
  icon: string;
  category: CardCategory;
  desc: string;
  /** Mutates the run modifiers in place when chosen. */
  apply: (m: RunModifiers) => void;
  /** Synergy tags reserved for Phase 2 card evolutions. */
  tags?: string[];
}

const ECONOMY_CARDS: AgeCard[] = [
  {
    id: 'trade-routes',
    name: 'Trade Routes',
    icon: '🛞',
    category: 'economy',
    desc: '+25% to all resource production.',
    apply: (m) => (m.productionMult *= 1.25),
    tags: ['production'],
  },
  {
    id: 'sharpened-tools',
    name: 'Sharpened Tools',
    icon: '🪚',
    category: 'economy',
    desc: '+35% harvest damage.',
    apply: (m) => (m.harvestDamageMult *= 1.35),
    tags: ['harvest'],
  },
  {
    id: 'wide-survey',
    name: 'Wide Survey',
    icon: '🧭',
    category: 'economy',
    desc: '+20% harvest circle radius.',
    apply: (m) => (m.harvestRadiusMult *= 1.2),
    tags: ['harvest'],
  },
  {
    id: 'guild-labor',
    name: 'Guild Labor',
    icon: '👷',
    category: 'economy',
    desc: '+45% auto-collection rate.',
    apply: (m) => (m.autoCollectMult *= 1.45),
    tags: ['auto'],
  },
  {
    id: 'efficient-masons',
    name: 'Efficient Masons',
    icon: '📐',
    category: 'economy',
    desc: 'Buildings cost 15% less.',
    apply: (m) => (m.buildCostMult *= 0.85),
    tags: ['build'],
  },
];

const MILITARY_CARDS: AgeCard[] = [
  {
    id: 'standing-army',
    name: 'Standing Army',
    icon: '⚔️',
    category: 'military',
    desc: '+40% defense and +10 flat defense.',
    apply: (m) => {
      m.defenseMult *= 1.4;
      m.defenseFlat += 10;
    },
    tags: ['defense'],
  },
  {
    id: 'stone-walls',
    name: 'Stone Walls',
    icon: '🧱',
    category: 'military',
    desc: '+22 flat defense.',
    apply: (m) => (m.defenseFlat += 22),
    tags: ['defense'],
  },
  {
    id: 'drilled-ranks',
    name: 'Drilled Ranks',
    icon: '🎖️',
    category: 'military',
    desc: '+20% defense and +8 flat defense.',
    apply: (m) => {
      m.defenseMult *= 1.2;
      m.defenseFlat += 8;
    },
    tags: ['defense'],
  },
  {
    id: 'war-economy',
    name: 'War Economy',
    icon: '🏹',
    category: 'military',
    desc: '+50% defense.',
    apply: (m) => (m.defenseMult *= 1.5),
    tags: ['defense'],
  },
  {
    id: 'watchtowers',
    name: 'Watchtowers',
    icon: '🗼',
    category: 'military',
    desc: '+15% defense and +12% production.',
    apply: (m) => {
      m.defenseMult *= 1.15;
      m.productionMult *= 1.12;
    },
    tags: ['defense', 'production'],
  },
];

const KNOWLEDGE_CARDS: AgeCard[] = [
  {
    id: 'scholars',
    name: 'Scholars',
    icon: '🎓',
    category: 'knowledge',
    desc: '+45% knowledge production and tech.',
    apply: (m) => (m.knowledgeMult *= 1.45),
    tags: ['knowledge'],
  },
  {
    id: 'scientific-method',
    name: 'Scientific Method',
    icon: '🔬',
    category: 'knowledge',
    desc: '+25% knowledge and +15% production.',
    apply: (m) => {
      m.knowledgeMult *= 1.25;
      m.productionMult *= 1.15;
    },
    tags: ['knowledge', 'production'],
  },
  {
    id: 'enlightenment',
    name: 'Enlightenment',
    icon: '💡',
    category: 'knowledge',
    desc: '+30% knowledge and +12% defense.',
    apply: (m) => {
      m.knowledgeMult *= 1.3;
      m.defenseMult *= 1.12;
    },
    tags: ['knowledge', 'defense'],
  },
  {
    id: 'mass-schooling',
    name: 'Mass Schooling',
    icon: '🏫',
    category: 'knowledge',
    desc: '+25% knowledge and +20% auto-collection.',
    apply: (m) => {
      m.knowledgeMult *= 1.25;
      m.autoCollectMult *= 1.2;
    },
    tags: ['knowledge', 'auto'],
  },
  {
    id: 'engineering',
    name: 'Engineering',
    icon: '⚙️',
    category: 'knowledge',
    desc: '+20% production and buildings cost 10% less.',
    apply: (m) => {
      m.productionMult *= 1.2;
      m.buildCostMult *= 0.9;
    },
    tags: ['production', 'build'],
  },
];

export const CARD_POOL: Record<CardCategory, AgeCard[]> = {
  economy: ECONOMY_CARDS,
  military: MILITARY_CARDS,
  knowledge: KNOWLEDGE_CARDS,
};

/** Look up any card by id (used when reapplying picks from a loaded save). */
export const CARD_BY_ID: Record<string, AgeCard> = {};
for (const list of Object.values(CARD_POOL)) {
  for (const card of list) CARD_BY_ID[card.id] = card;
}

// ---- Card synergies (Phase 2 power spikes) ----
// Synergies are not picked directly: they activate automatically once your
// accumulated card picks cross a threshold of categories. Crossing a threshold
// is a deliberate power spike, the Vampire Survivors "build comes online" beat.

export interface SynergyDef {
  id: string;
  name: string;
  icon: string;
  desc: string;
  /** Minimum count of each category among picked cards to activate. */
  requires: Partial<Record<CardCategory, number>>;
  apply: (m: RunModifiers) => void;
}

export const SYNERGIES: SynergyDef[] = [
  {
    id: 'industrial-revolution',
    name: 'Industrial Revolution',
    icon: '🏗️',
    desc: 'Two economy paths combine: +50% production.',
    requires: { economy: 2 },
    apply: (m) => (m.productionMult *= 1.5),
  },
  {
    id: 'fortress',
    name: 'Fortress',
    icon: '🏯',
    desc: 'Two military paths combine: +45% defense and +25 flat.',
    requires: { military: 2 },
    apply: (m) => {
      m.defenseMult *= 1.45;
      m.defenseFlat += 25;
    },
  },
  {
    id: 'great-library',
    name: 'Great Library',
    icon: '📜',
    desc: 'Two knowledge paths combine: +60% knowledge and +25% production.',
    requires: { knowledge: 2 },
    apply: (m) => {
      m.knowledgeMult *= 1.6;
      m.productionMult *= 1.25;
    },
  },
  {
    id: 'renaissance',
    name: 'Renaissance',
    icon: '🎨',
    desc: 'Economy meets knowledge: +30% production and +30% knowledge.',
    requires: { economy: 1, knowledge: 1 },
    apply: (m) => {
      m.productionMult *= 1.3;
      m.knowledgeMult *= 1.3;
    },
  },
  // Cross-category defense trimmed to 1.2x on the three below: a guaranteed
  // 2M/1E/1K spread used to stack Fortress + War Machine + Enlightened Guard +
  // Golden Age to ~3.7x defense (now ~2.5x), out-compounding the age threat
  // curve. Fortress keeps the big number as the pure-military identity.
  {
    id: 'war-machine',
    name: 'War Machine',
    icon: '⚙️',
    desc: 'Economy meets military: +20% defense and +25% production.',
    requires: { economy: 1, military: 1 },
    apply: (m) => {
      m.defenseMult *= 1.2;
      m.productionMult *= 1.25;
    },
  },
  {
    id: 'enlightened-guard',
    name: 'Enlightened Guard',
    icon: '🛡️',
    desc: 'Knowledge meets military: +20% defense and +30% knowledge.',
    requires: { military: 1, knowledge: 1 },
    apply: (m) => {
      m.defenseMult *= 1.2;
      m.knowledgeMult *= 1.3;
    },
  },
  {
    id: 'golden-age',
    name: 'Golden Age',
    icon: '👑',
    desc: 'All three in balance: +35% production and knowledge, +20% defense.',
    requires: { economy: 1, military: 1, knowledge: 1 },
    apply: (m) => {
      m.productionMult *= 1.35;
      m.defenseMult *= 1.2;
      m.knowledgeMult *= 1.35;
    },
  },
];

/** Counts how many of each category appear in a list of picked card ids. */
export function categoryCounts(pickedCards: string[]): Record<CardCategory, number> {
  const counts: Record<CardCategory, number> = { economy: 0, military: 0, knowledge: 0 };
  for (const id of pickedCards) {
    const card = CARD_BY_ID[id];
    if (card) counts[card.category] += 1;
  }
  return counts;
}

/** Returns the synergies currently active for a set of picks. */
export function activeSynergies(pickedCards: string[]): SynergyDef[] {
  const counts = categoryCounts(pickedCards);
  return SYNERGIES.filter((s) =>
    (Object.entries(s.requires) as [CardCategory, number][]).every(
      ([cat, need]) => counts[cat] >= need,
    ),
  );
}
